// src/contexts/CurrentUserContext/CurrentUserContext.tsx

import React, { createContext, useState, ReactNode, useEffect } from 'react';

import { IFreelancer } from '../data_models/freelancer';
import { IEmployer } from '../data_models/employer';
import { EUserType } from '../data_models/enums';
import { CurrentUserContextType } from './types/currentUserContextType';
import { useCookies } from 'react-cookie';
import ECookies from '../config/cookies';
import axios from 'axios';
import { CREDITS_ENDPOINTS, USER_ENDPOINTS, WALLET_ENDPOINTS } from '../config/apiEndpoints';

import { EmployerWallet } from '../data_models/employerWallet';
import { FreelancerWallet } from '../data_models/freelancerWallet';
import { uploadFileToSignedUrl } from '../utils/uploadFiles';
import { fromJSONSerialization } from '../data_models/helpers/serialization';
import { useSocket } from '../hooks/useSocket';
import useAuth from '../hooks/useAuth';



// Create the context
export const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

interface CurrentUserProviderProps {
    children: ReactNode;
    authToken: string | undefined;
}

// Provider component
export const CurrentUserProvider: React.FC<CurrentUserProviderProps> = ({ children, authToken }) => {

    const { registerUser } = useSocket();

    const [loading, setLoading] = useState<boolean>(false);
    const [profilePictureLoading, setProfilePictureLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [cookies, setCookie] = useCookies([
        ECookies.CURRENT_USER_COOKIE,
        ECookies.CURRENT_USER_TYPE_COOKIE,
        ECookies.TECH_AUTH_USER_COOKIE,
    ]);

    const [currentUser, setCurrentUser] = useState<IFreelancer | IEmployer | null>(
        fromJSONSerialization(cookies[ECookies.CURRENT_USER_COOKIE]) || null
    );
    const [currentUserType, setCurrentUserType] = useState<EUserType | null>(
        cookies[ECookies.CURRENT_USER_TYPE_COOKIE] as EUserType || null
    );
    const [wallet, setWallet] = useState<EmployerWallet | FreelancerWallet | null>(null);

    useEffect(() => {
        if (currentUser && currentUserType) {
            registerUser(currentUser._id!, currentUserType);
        } else if (!currentUser && currentUserType) {
            setCookie(ECookies.CURRENT_USER_TYPE_COOKIE, currentUserType, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
        }


        setIsLoggedIn(currentUser !== null && currentUserType !== null);
    }, [currentUser, currentUserType]);

    useEffect(() => {
        try {
            if (authToken) {
                const cookieType = cookies[ECookies.CURRENT_USER_TYPE_COOKIE];
                const cookieUser = cookies[ECookies.CURRENT_USER_COOKIE];
                if (cookieType && cookieUser) {
                    setCurrentUserType(cookieType as EUserType);
                    switch (cookieType as EUserType) {
                        case EUserType.FREELANCER:
                            setCurrentUser(fromJSONSerialization(cookieUser) as IFreelancer);
                            break;
                        case EUserType.EMPLOYER:
                            setCurrentUser(fromJSONSerialization(cookieUser) as IEmployer);
                            break;
                        default:
                            setCurrentUser(fromJSONSerialization(cookieUser));
                            break;
                    }
                }

                fetchCurrentUser().then((user) => {
                    if (user) {
                        setCurrentUser(fromJSONSerialization(user));
                    }
                }).catch((error) => {
                    console.error(error);
                });
            } else {
                setCurrentUser(null);
            }
        } catch (error) {
            console.error(error)
        }

    }, [authToken]);

    useEffect(() => {
        try {
            const type = cookies[ECookies.CURRENT_USER_TYPE_COOKIE];
            const user = cookies[ECookies.CURRENT_USER_COOKIE];

            if (type && user) {
                switch (type as EUserType) {
                    case EUserType.FREELANCER:
                        setCurrentUserType(EUserType.FREELANCER);
                        setCurrentUser(fromJSONSerialization(user) as IFreelancer);
                        break;
                    case EUserType.EMPLOYER:
                        setCurrentUserType(EUserType.EMPLOYER);
                        setCurrentUser(fromJSONSerialization(user) as IEmployer);
                        break;
                    default:
                        setCurrentUserType(type);
                        setCurrentUser(fromJSONSerialization(user));
                        break;
                }
            } else if (!type && !user) {
                setCurrentUser(null);
                setCurrentUserType(null);
            }
        } catch (error) {
            console.error("CurrentUserProvider [techcookie]", error)
        }

    }, [cookies[ECookies.TECH_AUTH_USER_COOKIE]]);

    const clearError = () => setError(null);
    const handleApiError = (error: any) => {
        if (axios.isAxiosError(error) && error.response) {
            setError(error.response.data.error || 'An error occurred');
        } else if (error instanceof Error) {
            setError(error.message);
        } else {
            setError('An unexpected error occurred');
        }
    };


    const fetchCurrentUser = async (): Promise<IEmployer | IFreelancer | null> => {
        clearError();
        setLoading(true);
        try {
            const response = await axios.get(USER_ENDPOINTS.GET_ME_FULL, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });

            const user = response.data as IFreelancer | IEmployer;
            setCurrentUser(fromJSONSerialization(user));
            await fetchWallet();

            // update cookie
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            return fromJSONSerialization(user);
        } catch (e) {
            handleApiError(e);
            return null;
            // throw error;
        } finally {
            setLoading(false);
        }
    }

    const updateCurrentUser = async (user: IFreelancer | IEmployer): Promise<IFreelancer | IEmployer | null> => {
        clearError();
        setLoading(true);

        try {
            const response = await axios.put(USER_ENDPOINTS.PUT_ME, { updateObj: user }, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            const updatedUser = response.data;
            setCurrentUser(fromJSONSerialization(updatedUser));

            // update cookie
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(updatedUser), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            return fromJSONSerialization(updatedUser);
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }
    const locallyUpdateCurrentUser = (user: IFreelancer | IEmployer) => {
        setCurrentUser(user);
        setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
            path: '/',
            secure: true,
            sameSite: 'strict',
        });
    }
    const locallyUpdateCurrentUserWallet = (wallet: EmployerWallet | FreelancerWallet) => {
        setWallet(wallet);
    }
    const updateProfilePicture = async (file: File): Promise<IEmployer | IFreelancer | null> => {
        clearError();
        setProfilePictureLoading(true);
        try {

            // Get signed URL
            const response = await axios.get(USER_ENDPOINTS.GET_SIGNED_URL_PROFILE_PICTURE, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    contentType: file.type,
                    filename: file.name,
                },
            });

            const signedUrl = response.data;


            // upload file to signed URL
            await uploadFileToSignedUrl(file, signedUrl);

            // Confirm file upload
            const confirmResponse = await axios.post(USER_ENDPOINTS.POST_CONFIRM_PROFILE_PICTURE_UPLOAD, {
                filename: file.name,
            }, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });

            const user = confirmResponse.data as IFreelancer | IEmployer;

            setCurrentUser(fromJSONSerialization(user));

            // update cookie
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            return fromJSONSerialization(user);
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setProfilePictureLoading(false);
        }
    }

    const fetchWallet = async () => {
        clearError();
        setLoading(true);
        try {
            const response = await axios.get(WALLET_ENDPOINTS.GET_WALLET, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });

            const wallet = response.data as EmployerWallet | FreelancerWallet;

            setWallet(fromJSONSerialization(wallet));
            return wallet;
        } catch (e) {
            handleApiError(e);
            throw e;
        } finally {
            setLoading(false);
        }
    }

    const fetchPmPayoutAfterTaskCompleted = async (taskId: string) => {
        try {
            const response = await axios.get(`${CREDITS_ENDPOINTS.GET_PM_PAYOUT_AFTER_TASK_COMPLETED}/${taskId}`,
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error('An unexpected error occurred');
        }
    }

    const fetchPmPayoutAfterProjectCompleted = async (projectId: string) => {
        try {
            const response = await axios.get(`${CREDITS_ENDPOINTS.GET_PM_PAYOUT_AFTER_PROJECT_COMPLETED}/${projectId}`,
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error('An unexpected error occurred');
        }
    }

    return (
        <CurrentUserContext.Provider value={{
            loading,
            profilePictureLoading,
            error,
            isLoggedIn,
            currentUser,
            setCurrentUser,
            currentUserType,
            setCurrentUserType,
            wallet,
            setWallet,
            fetchCurrentUser,
            updateCurrentUser,
            locallyUpdateCurrentUser,
            locallyUpdateCurrentUserWallet,
            updateProfilePicture,
            fetchWallet,
            fetchPmPayoutAfterTaskCompleted,
            fetchPmPayoutAfterProjectCompleted,
        }}>
            {children}
        </CurrentUserContext.Provider>
    );
};
